// The seaquake tremor clock (R101, re-shaped by the settlement work).
//
// Bursts, envelope and camera offset — no renderer in any of it, so the rules
// worth pinning (rare bursts at stages 1–2, rapid escalating shaking through
// the settlement, reduced motion dropping the shudder and keeping the silt)
// are testable without a GPU, and a second renderer can drive the same clock
// without importing the layer that owns the silt.
//
// The shape is deliberately back-loaded. Stages 1 and 2 run for days of world
// time, so a shudder there is a gimmick, not a nuisance: one every few
// minutes, punchy when it comes, and quiet the rest of the time. The
// settlement (stage 3) is fifteen minutes long and is the world ending, so it
// is the one place the screen really moves — bursts arrive faster than they
// ring down, so the shaking is continuous, and it escalates.

import { reducedMotion } from './motion';
import { clamp01, lerp } from './recipe-kit';

// --- invented figures, all of them; the values and their rationale ride
// data/presentation/presentation.toml [presentation.quake] (R101 and the
// settlement entries), generated into gen/presentation.ts.
import {
  QUAKE_BURST_AMPLITUDE_PX as BURST_AMPLITUDE_PX,
  QUAKE_BURST_EVERY_SEC as BURST_EVERY_SEC,
  QUAKE_BURST_FREQ_HZ as BURST_FREQ_HZ,
  QUAKE_BURST_SEC as BURST_SEC,
  QUAKE_SETTLE_AMPLITUDE_PX as SETTLE_AMPLITUDE_PX,
  QUAKE_SETTLE_BURST_SEC as SETTLE_BURST_SEC,
  QUAKE_SETTLE_EVERY_SEC as SETTLE_EVERY_SEC,
  QUAKE_SETTLE_RAMP_SEC as SETTLE_RAMP_SEC,
  QUAKE_SETTLE_SILT_PER_BURST as SETTLE_SILT_PER_BURST,
  QUAKE_SILT_PER_BURST as SILT_PER_BURST,
  QUAKE_VY_AMP_FRAC as VY_AMP_FRAC,
  QUAKE_VY_FREQ_RATIO as VY_FREQ_RATIO,
} from '../gen/presentation';

/**
 * The tremor clock: bursts, envelope, camera offset — no engine in it, so the
 * rules worth pinning (rare bursts at stages 1–2, rapid escalating shaking
 * through the settlement, reduced motion dropping the shudder and keeping
 * the silt) are testable without a GPU.
 */
export class Tremor {
  /** Camera shudder, in screen px. Zero whenever the sea is settled. */
  offsetX = 0;
  offsetY = 0;

  private time = 0;
  private sinceBurst = 0;
  private burstLeft = 0;
  private burstLen = 1;
  private amplitude = 0;
  /** Seconds spent at stage 3 so far — the settlement's own ramp clock. */
  private settling = 0;

  constructor(private noShake = reducedMotion()) {}

  /**
   * Advances the clock one frame and returns how many grains of silt this
   * frame shook loose — 0 on all but the first frame of a burst.
   *
   * @param stage `Scoreboard.quakeStage`: 1 a rare tremble, 2 a rare hard
   * one, 3 the settlement (rapid and escalating), 0 calm.
   * @param settleT settlement progress 0..1 off the wire countdown (R126),
   * or null before the first scoreboard — then a client-side clock ramps
   * instead.
   */
  step(dt: number, stage: number, settleT: number | null = null): number {
    this.time += dt;
    let silt = 0;
    if (stage >= 3) {
      // The settlement escalates on the server's real clock; the internal
      // clock is only the fallback for the frames before the first board.
      this.settling += dt;
      const t = settleT !== null
        ? clamp01(settleT)
        : Math.min(1, this.settling / SETTLE_RAMP_SEC);
      this.sinceBurst += dt;
      if (this.sinceBurst >= lerp(SETTLE_EVERY_SEC[0], SETTLE_EVERY_SEC[1], t)) {
        this.startBurst(
          SETTLE_BURST_SEC, lerp(SETTLE_AMPLITUDE_PX[0], SETTLE_AMPLITUDE_PX[1], t),
        );
        silt = Math.round(lerp(SETTLE_SILT_PER_BURST[0], SETTLE_SILT_PER_BURST[1], t));
      }
    } else if (stage === 1 || stage === 2) {
      this.settling = 0;
      this.sinceBurst += dt;
      if (this.sinceBurst >= BURST_EVERY_SEC[stage]) {
        this.startBurst(BURST_SEC[stage], BURST_AMPLITUDE_PX[stage]);
        silt = SILT_PER_BURST[stage];
      }
    } else {
      // Calm water: no clock, no ring-down.
      this.settling = 0;
      this.sinceBurst = 0;
      this.burstLeft = 0;
    }

    // The burst clock runs either way; only the offset it produces is
    // suppressed. `prefers-reduced-motion` drops the shudder and keeps the
    // silt — the quake is still legible, it just stops moving the screen.
    if (this.burstLeft > 0) this.burstLeft -= dt;
    if (this.burstLeft > 0 && !this.noShake) {
      const t = this.burstLeft / this.burstLen; // 1 → 0
      const env = t * t; // the jolt hits first and rings down
      const w = 2 * Math.PI * BURST_FREQ_HZ * this.time;
      this.offsetX = Math.sin(w) * this.amplitude * env;
      this.offsetY = Math.sin(w * VY_FREQ_RATIO + 1.1) * this.amplitude * env * VY_AMP_FRAC;
    } else {
      this.offsetX = 0;
      this.offsetY = 0;
    }
    return silt;
  }

  /**
   * Starts one burst of `stage` now, with the figures of that stage, and
   * returns the grains of silt it shakes loose (R1535). The reel page calls
   * it, because a mode reel needs its shock at a scripted second and not
   * at the next turn of the clock. The burst rings down only while the
   * caller feeds a stage over 0 to `step`. Stage 3 takes the figures of the
   * settlement at its start.
   */
  shock(stage: 1 | 2 | 3): number {
    if (stage === 3) {
      this.startBurst(SETTLE_BURST_SEC, SETTLE_AMPLITUDE_PX[0]);
      return SETTLE_SILT_PER_BURST[0];
    }
    this.startBurst(BURST_SEC[stage], BURST_AMPLITUDE_PX[stage]);
    return SILT_PER_BURST[stage];
  }

  /** Starts one burst of `len` seconds and restarts the wait for the next. */
  private startBurst(len: number, amplitude: number): void {
    this.sinceBurst = 0;
    this.burstLen = len;
    this.burstLeft = len;
    this.amplitude = amplitude;
  }
}
